import { createContext, type ReactNode, useContext } from 'react'
import { type MotionValue, useMotionValue } from 'motion/react'

/**
 * Can be used to provide the animation progress value from 0 to 1.
 * Used by scroll-based animations.
 */
const AnimationProgressContext = createContext<MotionValue<number> | undefined>(
  undefined,
)

/**
 * Hook for consuming the animation progress context.
 *
 * @param valueFromProp if specified, uses this. Otherwise uses the value from the context.
 */
export function useAnimationProgress(
  valueFromProp?: AnimationProgressProviderProps['progress'],
): MotionValue<number> {
  const fromContext = useContext(AnimationProgressContext)
  const defaultValue = useMotionValue(0)
  return valueFromProp ?? fromContext ?? defaultValue
}

/**
 * Props for the context provider.
 */
interface AnimationProgressProviderProps {
  progress: MotionValue<number> | undefined
  children?: ReactNode
}

/**
 * Provides the animation progress value.
 * Can be used by intermediate components to forward the one passed through props.
 */
export function AnimationProgressProvider({
  progress,
  children,
}: AnimationProgressProviderProps) {
  if (typeof progress === 'undefined') {
    return <>{children}</>
  }

  return (
    <AnimationProgressContext.Provider value={progress}>
      {children}
    </AnimationProgressContext.Provider>
  )
}
